"use client";

import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronDown, Copy, MoreHorizontal, ReceiptText } from "lucide-react";
import {
  JsonView,
  collapseAllNested,
  darkStyles,
} from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";

import { OrderItem } from "@/schemas/order";
import { useSelectedOrder } from "@/context/selected-order-context";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { PizzaDTO } from "@/api/custom-schema";
import { StatusBadge } from "@/components/status-badge";
import { PizzaThumbnail } from "@/components/pizza-thumbnail";
import { formatCaptureCount, formatDailyCounter } from "@/lib/counter-format";
import { formatOrderStatus } from "@/lib/order-status";
import { parsePizzaName, splitOrderItems } from "@/lib/order-items";
import { pizzaVerdict } from "@/lib/pizza-verdict";
import { cn } from "@/lib/utils";
import { useOrderPizzas } from "./hooks/use-order-pizzas";
import { useOrderNavigation } from "./hooks/use-order-navigation";
import { isInteractiveTarget } from "./hooks/use-pizza-navigation";
import { NavigationGroup, type DetailNavigation } from "@/pizza-detail/detail-header";
import { copyToClipboard } from "@/lib/clipboard";
import { OrderBranchName } from "./columns";
import { shortDateTime } from "@/lib/date-format";
import { useLocalization } from "@/context/localization-context";

/** A pizza of the order: photo, verdict and, for a NOK, what failed. */
function PizzaTile({
  item,
  pizza,
  loading,
  onOpen,
}: {
  item: OrderItem;
  pizza?: PizzaDTO;
  loading: boolean;
  onOpen: () => void;
}) {
  const { t } = useLocalization();
  const metadata = pizza?.evaluation?.metadata;
  const verdict = pizzaVerdict(metadata);
  const { typeId, name } = parsePizzaName(item.name);
  const label =
    verdict.kind === "no_pizza"
      ? t("status.noPizza")
      : verdict.kind === "invalid_photo"
        ? t("status.invalidShort")
        : verdict.kind === "not_evaluated"
          ? pizza
            ? t("status.notEvaluated")
            : t("orders.tile.notCaptured")
          : verdict.status === "OK"
            ? t("status.ok")
            : verdict.status === "NOK"
              ? t("status.nok")
              : t("status.na");

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex flex-col overflow-hidden rounded-lg border bg-background text-left transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="relative">
        <PizzaThumbnail
          pizzaId={item.id}
          roi={metadata?.pizza_roi}
          enabled={!!pizza}
          scale={15}
          className="w-full transition-transform duration-300 group-hover:scale-[1.02]"
        />
        {(pizza || !loading) && (
          <StatusBadge
            status={verdict.status === "N/A" ? null : verdict.status}
            label={label}
            tone={verdict.kind === "ok" ? "ok" : verdict.kind === "not_evaluated" ? "neutral" : "nok"}
            className="absolute right-2 top-2 bg-background/80 shadow backdrop-blur"
          />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-2.5">
        <div className="flex min-w-0 items-center gap-1.5">
          {typeId && (
            <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded bg-muted px-1 text-[11px] font-bold tabular-nums">
              {typeId}
            </span>
          )}
          <span className="truncate text-sm font-medium" title={item.name}>
            {name}
          </span>
        </div>
        {verdict.reasons.length > 0 && verdict.kind !== "ok" ? (
          <div className="line-clamp-2 text-xs text-red-300/80" title={verdict.reasons.join(", ")}>
            {verdict.reasons.join(", ")}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            {pizza
              ? t("orders.tile.captured", {
                  date: shortDateTime(pizza.capture_at),
                  count: formatCaptureCount(pizza.capture_count),
                })
              : loading
                ? " "
                : t("orders.tile.noPhoto")}
          </div>
        )}
      </div>
    </button>
  );
}

interface OrderDetailProps {
  /** back to the list on narrow screens, where the list and the detail are not side by side */
  onBack?: () => void;
  /** ←/→ open the previous / next order of the list */
  keyboardNavigation?: boolean;
  className?: string;
}

export default function OrderDetail({
  onBack,
  keyboardNavigation = true,
  className,
}: OrderDetailProps) {
  const navigate = useNavigate();
  const { t, tp } = useLocalization();
  const { selectedOrder, selectedOrderId, loading } = useSelectedOrder();

  const [pizzas, otherItems] = useMemo(
    () => splitOrderItems(selectedOrder?.items),
    [selectedOrder],
  );
  const { pizzasById, isLoading: pizzasLoading } = useOrderPizzas(pizzas.map((item) => item.id));

  const orderNav = useOrderNavigation(selectedOrder);
  const goToOrder = (direction: "next" | "prev") =>
    orderNav.move(direction, (order) => navigate(`/orders/${order.id}`));
  const nav: DetailNavigation = {
    position: orderNav.position,
    total: orderNav.total,
    unit: tp("orders.list.unit", orderNav.total ?? 0),
    hasPrev: orderNav.hasPrev,
    hasNext: orderNav.hasNext,
    onPrev: () => goToOrder("prev"),
    onNext: () => goToOrder("next"),
    prevLabel: t("orders.detail.previousOrder"),
    nextLabel: t("orders.detail.nextOrder"),
    busy: orderNav.moving,
  };
  const goRef = useRef(goToOrder);
  useEffect(() => {
    goRef.current = goToOrder;
  });
  useEffect(() => {
    if (!keyboardNavigation || !selectedOrderId) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (isInteractiveTarget(e.target)) return;
      if (e.key === "ArrowRight") goRef.current("next");
      else if (e.key === "ArrowLeft") goRef.current("prev");
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [keyboardNavigation, selectedOrderId]);

  const handlePizzaClick = (item: OrderItem) => {
    navigate(`/orders/${selectedOrder?.id}/pizza/${item.id}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!selectedOrderId) {
    return (
      <section
        className={cn(
          "flex min-h-64 flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-card/50 p-8 text-center",
          className,
        )}
      >
        <ReceiptText className="h-8 w-8 text-muted-foreground/60" />
        <div className="text-sm font-medium">{t("orders.detail.noSelection")}</div>
        <div className="max-w-56 text-xs text-muted-foreground">{t("orders.detail.noSelectionHint")}</div>
      </section>
    );
  }

  if (!selectedOrder) {
    return (
      <section className={cn("rounded-xl border bg-card p-4", className)}>
        <div className="h-7 w-40 animate-pulse rounded bg-muted" />
        <div className="mt-2 h-4 w-64 animate-pulse rounded bg-muted" />
        <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="aspect-square animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
        {!loading && (
          <div className="mt-4 text-sm text-muted-foreground">{t("orders.detail.loadFailed")}</div>
        )}
      </section>
    );
  }

  const evaluated = pizzas
    .map((item) => pizzasById.get(item.id)?.evaluation?.metadata?.overall_evaluation?.status)
    .filter(Boolean);
  const okCount = evaluated.filter((status) => status === "OK").length;
  const nokCount = evaluated.filter((status) => status === "NOK").length;
  const branchId = (selectedOrder as unknown as { location?: { id?: string } }).location?.id;

  return (
    <section className={cn("overflow-hidden rounded-xl border bg-card", className)} aria-label={t("orders.detail.label")}>
      <header className="flex items-start gap-2 border-b p-3 sm:gap-3 sm:p-4">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            className="-ml-1 h-9 w-9 shrink-0"
            onClick={onBack}
            aria-label={t("orders.detail.back")}
          >
            <ArrowLeft />
          </Button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-mono text-xl font-bold leading-tight tracking-tight">
              {formatDailyCounter(selectedOrder.daily_counter ?? selectedOrder.formatted_id)}
            </h2>
            <span className="rounded-md border px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
              {formatOrderStatus(selectedOrder.status)}
            </span>
            {evaluated.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {nokCount > 0 ? (
                  <>
                    <span className="font-semibold text-red-400">
                      {t("orders.detail.nokCount", { count: nokCount })}
                    </span>{" "}
                    {t("orders.detail.ofTotal", { total: pizzas.length })}
                  </>
                ) : (
                  <span className="text-green-400/90">{tp("orders.detail.allOk", okCount)}</span>
                )}
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-muted-foreground sm:text-sm">
            <OrderBranchName branchId={branchId} />
            <span aria-hidden className="text-muted-foreground/40">•</span>
            <span className="tabular-nums">
              {t("orders.detail.sent", { date: shortDateTime(selectedOrder.sent_at) })}
            </span>
            <span aria-hidden className="hidden text-muted-foreground/40 sm:inline">•</span>
            <span className="hidden tabular-nums sm:inline">
              {t("orders.detail.created", { date: shortDateTime(selectedOrder.created_at) })}
            </span>
          </div>
        </div>
        <NavigationGroup nav={nav} className="hidden sm:flex" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label={t("orders.actions.menuLabel")}>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => copyToClipboard(selectedOrder.id, t("orders.columns.orderId"))}>
              <Copy className="h-4 w-4" />
              {t("orders.actions.copyId")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => copyToClipboard(JSON.stringify(selectedOrder), t("orders.actions.orderData"))}
            >
              <Copy className="h-4 w-4" />
              {t("orders.actions.copyData")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="flex flex-col gap-4 p-3 sm:p-4">
        {/* order navigation on phones, where the header has no room for it */}
        <NavigationGroup nav={nav} className="justify-between sm:hidden" />

        {pizzas.length > 0 ? (
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("orders.detail.pizzasHeading", { count: pizzas.length })}
            </h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(10.5rem,1fr))]">
              {pizzas.map((item) => (
                <PizzaTile
                  key={item.id}
                  item={item}
                  pizza={pizzasById.get(item.id)}
                  loading={pizzasLoading}
                  onOpen={() => handlePizzaClick(item)}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
            {t("orders.detail.noPizzas")}
          </div>
        )}

        {otherItems.length > 0 && (
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("orders.detail.otherItems", { count: otherItems.length })}
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {otherItems.map((item) => (
                <span
                  key={item.id}
                  className="max-w-full truncate rounded-md border bg-muted/40 px-2 py-1 text-xs"
                  title={item.name}
                >
                  {item.quantity > 1 && <span className="mr-1 font-semibold">{item.quantity}×</span>}
                  {item.name}
                </span>
              ))}
            </div>
          </div>
        )}

        <Collapsible className="group rounded-lg border">
          <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium">
            {t("orders.actions.orderData")}
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="max-h-96 overflow-auto border-t text-xs">
              <JsonView
                data={selectedOrder as object}
                shouldExpandNode={collapseAllNested}
                style={{ ...darkStyles, container: "p-3 font-mono bg-transparent" }}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </section>
  );
}
